import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getDataForSelectFilters } from "../../../../../services/restAPI/ListsV2";
import { Table } from "antd";
import { useFormV3 } from "../../../../../context/FormV3Context/FormV3Context";
import { Button, Checkbox } from "../../../../BP2Components";
import { useIntl } from "react-intl";
import { Column, EditedRowPatch, FormV3ListTableProps, RowRecord } from "./types";
import { format } from "date-fns";
import { ColumnProps } from "antd/lib/table";
import { isFieldDisabled } from "../FormFIeldsV3Decorators";
import { useListTableRowForm } from "../../../../../util/useListTableRowForm";
import { renderFormField } from "../../renderFormField";
import { useFieldController } from "../../../../../util/useFieldController";
import { hasValue } from "../../../../../util/pocUtils";
import { propertiesElement } from "../../../ListBuilder/ListBuildertypes";

const EMPTY_DEFAULT_FILTER = Object.freeze({} as Record<string, propertiesElement>);

export type RangeValidationReason = "EMPTY" | "MIN" | "MAX";

export type RangeValidationProblem = {
   fieldId: string;
   reason: RangeValidationReason;
   min?: number;
   max?: number;
};

const toDisplayValue = (value: any) => {
   if (value == null) return "";

   if (Array.isArray(value)) {
      return value.map(toDisplayValue).filter(Boolean).join(", ");
   }

   if (typeof value === "object") {
      if ("label" in value) return String(value.label ?? "");
      if ("decoratedValue" in value) return String(value.decoratedValue ?? "");
      if ("originalValue" in value) return String(value.originalValue ?? "");
      return "";
   }

   return String(value);
};
const EMPTY_EDIT: EditedRowPatch = { __raw: {} };

export const FormV3ListTable = ({
   field,
   formGUID,
   selectedIds,
   onSelectionChange,
   setIsRecordEditing,
   hasCheckboxColumnById,
   setHasCheckboxForListTable,
   registerListTableValidator,
   unregisterListTableValidator,
}: FormV3ListTableProps) => {
   const [dataSource, setDataSource] = useState<RowRecord[]>([]);
   const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
   const [editedRow, setEditedRow] = useState<EditedRowPatch>(EMPTY_EDIT);
   const [apiColumns, setApiColumns] = useState<Column[]>([]);
   const [presenterFormId, setPresenterFormId] = useState<string>("");
   const { visibleColumns, fieldDefById, getRowFormGUID, buildFieldWithValue } =
      useListTableRowForm({ presenterFormId, apiColumns });

   const intl = useIntl();
   const { fieldInfo } = useFieldController({ field, formGUID });

   const { changeFieldValue, changeFieldsInfo, formV3Data, getFormFieldsByGUID } = useFormV3();

   const defaultFilter = useMemo(
      () =>
         (field.modelField?.list?.defaultFilter as Record<string, propertiesElement> | undefined) ??
         EMPTY_DEFAULT_FILTER,
      [field.modelField?.list?.defaultFilter],
   );

   const activeFilterParamsJson = useMemo(() => {
      const hasDefaultFilter = Object.keys(defaultFilter).length > 0;
      if (!hasDefaultFilter) return null;

      const formFields = getFormFieldsByGUID(formGUID);
      const params: Record<string, string> = {};
      for (const [listColumn, formFieldName] of Object.entries(defaultFilter)) {
         const formField = formFields.find(
            (formField) =>
               formField.modelField?.name === formFieldName.techName ||
               formField.name === formFieldName.techName,
         );
         if (!formField) continue;
         const value = formV3Data[formGUID]?.[formField.id];
         if (value != null && value !== "") {
            if (typeof value === "object") {
               const primitive = value?.originalValue ?? value?.value;
               if (primitive != null) {
                  params[listColumn] = String(primitive);
               }
            } else {
               params[listColumn] = String(value);
            }
         }
      }

      return JSON.stringify(params);
   }, [formV3Data, formGUID, defaultFilter, getFormFieldsByGUID]);
   const allIds = dataSource.map((row) => row.id);
   const stringSelectedIds = (selectedIds ?? []).map((id) => String(id));

   const hasCheckboxColumn = !!hasCheckboxColumnById?.[field.id];

   const allChecked =
      hasCheckboxColumn &&
      allIds.length > 0 &&
      allIds.every((id) => stringSelectedIds.includes(String(id)));

   const stringAllIds = allIds.map(String);
   const someChecked =
      hasCheckboxColumn &&
      stringAllIds.length > 0 &&
      stringSelectedIds.some((id) => stringAllIds.includes(id));

   const handleRowCheckboxChange = (row: RowRecord, checked: boolean) => {
      changeFieldsInfo?.(formGUID, { [field.id]: null });
      if (!onSelectionChange) return;
      const rowId = String(row.id);
      const prev = stringSelectedIds;

      const next = checked
         ? prev.includes(rowId)
            ? prev
            : [...prev, rowId]
         : prev.filter((id) => id !== rowId);

      onSelectionChange(next);
   };

   const handleHeaderCheckboxChange = (checked: boolean) => {
      changeFieldsInfo?.(formGUID, { [field.id]: null });

      if (!onSelectionChange) return;
      const nextIds = checked ? allIds.map((id) => String(id)) : [];
      onSelectionChange(nextIds);
   };

   const handleEdit = (record: RowRecord) => {
      setEditingRowKey(record.key);
      setEditedRow({
         ...record,
         __raw: { ...(record.__raw ?? {}) },
      });
      setIsRecordEditing(true);
   };

   const handleSave = (key: string) => {
      const updatedData = dataSource.map((row) => {
         if (row.key !== key) return row;

         return {
            ...row,
            ...editedRow,
            __raw: {
               ...(row.__raw ?? {}),
               ...(editedRow.__raw ?? {}),
            },
         };
      });

      setDataSource(updatedData);
      setEditingRowKey(null);
      setEditedRow(EMPTY_EDIT);
      changeFieldValue(formGUID, field.id, updatedData);
      setIsRecordEditing(false);
   };

   const processListResponse = useCallback(
      (responseData: any) => {
         const { columns: apiColumns, data, meta } = responseData;

         setHasCheckboxForListTable?.(field.id, !!meta?.hasCheckboxColumn);
         setApiColumns(apiColumns);
         setPresenterFormId(meta.global.presenterFormId);

         const dateColumns = apiColumns.filter(
            (column: Column) => column.meta?.type === "LocalDateTime",
         );
         const formattedData = data.map((row) => {
            const formattedRow = { ...row };
            dateColumns.forEach((column: Column) => {
               const raw = formattedRow[column.label];
               if (!raw) return;

               const parsed = new Date(raw);
               if (isNaN(parsed.getTime())) return;

               formattedRow[column.label] = format(parsed, "yyyy-MM-dd");
            });
            return formattedRow;
         });

         const rowData = formattedData.map((row: RowRecord) => ({
            ...row,
            key: row.id,
            __raw: {},
         }));
         setDataSource(rowData);
         changeFieldValue(formGUID, field.id, rowData);
      },
      [field.id, formGUID, changeFieldValue, setHasCheckboxForListTable],
   );

   useEffect(() => {
      let cancelled = false;

      const params =
         activeFilterParamsJson !== null
            ? (JSON.parse(activeFilterParamsJson) as Record<string, string>)
            : {};
      const url = (() => {
         if (Object.keys(params).length === 0) return field.listUrl;
         const [base, existing = ""] = field.listUrl.split("?");
         const merged = new URLSearchParams(existing);
         Object.entries(params).forEach(([key, value]) => merged.set(key, value));
         return `${base}?${merged.toString()}`;
      })();

      getDataForSelectFilters(url)
         .then((response) => {
            if (!cancelled) processListResponse(response.data);
         })
         .catch(() => {
            if (!cancelled) setDataSource([]);
         });

      return () => {
         cancelled = true;
      };
   }, [field.listUrl, activeFilterParamsJson, processListResponse]);

   useEffect(() => {
      return () => {
         setHasCheckboxForListTable?.(field.id, false);
      };
   }, [field.id, setHasCheckboxForListTable]);

   const getRangeMin = (fieldDef: any) => {
      const decorators = (fieldDef?.decorators ?? []).find(
         (decorator: any) => decorator.name === "RangeDecorator",
      );
      const min = decorators?.properties?.min;
      return min == null ? null : Number(min);
   };

   const getRangeMax = (
      fieldDef: any,
      row: RowRecord | undefined,
      fieldDefById: Record<string, any>,
      apiColumns: Column[],
   ) => {
      const decorators = (fieldDef?.decorators ?? []).find(
         (decorator: any) => decorator.name === "RangeDecorator",
      );
      const maxRaw = decorators?.properties?.max;
      if (maxRaw == null) return null;
      if (typeof maxRaw === "number") return maxRaw;
      if (typeof maxRaw === "string" && !maxRaw.startsWith("{")) {
         const maxValue = Number(maxRaw);
         return Number.isFinite(maxValue) ? maxValue : null;
      }

      if (!row) return null;

      if (typeof maxRaw === "string") {
         const findHid = maxRaw.match(/^\{(.+)\}$/);
         const hid = findHid?.[1];
         if (!hid) return null;
         const findField = Object.values(fieldDefById ?? {}).find((field) => field.hid === hid);
         if (!findField) return null;
         const fieldCol = (apiColumns ?? []).find(
            (col) => String(col.techName) === String(findField.id),
         );
         const colLabel = fieldCol?.label;
         const raw = row.__raw?.[String(findField.id)] ?? (colLabel ? row[colLabel] : undefined);
         const number =
            typeof raw === "object" && raw !== null
               ? Number(raw.value ?? raw.originalValue ?? raw.decoratedValue ?? NaN)
               : Number(raw);

         return Number.isFinite(number) ? number : null;
      }

      return null;
   };

   const getFieldLabel = (fieldId: string) => {
      const col = apiColumns.find((column) => String(column.techName) === String(fieldId));

      if (col?.label) return col.label;

      const def = fieldDefById?.[fieldId];
      return def?.label ?? fieldId;
   };

   const getOptionsRangeForListTable = () => {
      const decorators = (field as any)?.decorators ?? [];
      const decorator = decorators.find(
         (decorator: any) => decorator.name === "OptionsRangeDecorator",
      );

      if (!decorator) return { has: false, min: null, max: null };

      const min = decorator.properties?.min ?? null;
      const max = decorator.properties?.max ?? null;

      return {
         has: true,
         min: min == null ? null : Number(min),
         max: max == null ? null : Number(max),
      };
   };

   const getRowFieldValue = (row: RowRecord, fieldId: string, fieldLabel?: string) => {
      if (row.__raw && fieldId in row.__raw) {
         return row.__raw[fieldId];
      }

      if (fieldLabel && fieldLabel in row) {
         return row[fieldLabel];
      }

      return undefined;
   };

   const buildMessages = (details: Record<string, RangeValidationProblem[]>): string[] => {
      const errorsArray: string[] = [];

      for (const rowId of Object.keys(details)) {
         const errors = details[rowId] ?? [];

         const rowIndex = dataSource.findIndex((r) => String(r.id) === String(rowId));
         const row = rowIndex >= 0 ? rowIndex + 1 : rowId;

         for (const error of errors) {
            const fieldLabel = getFieldLabel(error.fieldId);
            if (error.reason === "EMPTY") {
               errorsArray.push(
                  `Wiersz ${row}: Pole "${fieldLabel}" musi mieć uzupełnioną wartość`,
               );
            }
            if (error.reason === "MIN") {
               errorsArray.push(
                  `Wiersz ${row}: Wartość minimalna dla pola "${fieldLabel}" to ${error.min}.`,
               );
            }
            if (error.reason === "MAX") {
               errorsArray.push(
                  `Wiersz ${row}: Wartość maksymalna dla pola "${fieldLabel}" to ${error.max}.`,
               );
            }
         }
      }

      return errorsArray;
   };

   const isRowTouched = (row: RowRecord, fields: any[]) => {
      const raw = row.__raw ?? {};
      return fields.some((fieldDef) => hasValue(raw[String(fieldDef.id)]));
   };

   const hasRequiredDecorator = (fieldDef: any) => {
      const decorators = fieldDef?.decorators ?? [];
      return decorators.some(
         (decorator: any) =>
            decorator.name === "RequiredDecorator" && decorator.properties?.required === true,
      );
   };

   const getRequiredFields = (rangedFields: any[]) => {
      return rangedFields.filter((fieldDef) => hasRequiredDecorator(fieldDef));
   };

   const validateRow = (row: RowRecord, rangedFields: any[]) => {
      const problems: RangeValidationProblem[] = [];

      for (const fieldDef of rangedFields) {
         const fieldId = String(fieldDef.id);
         const fieldLabel = getFieldLabel(fieldId);
         const value = getRowFieldValue(row, fieldId, fieldLabel);

         const isRequired = hasRequiredDecorator(fieldDef);

         if (!hasValue(value)) {
            if (isRequired) {
               problems.push({ fieldId, reason: "EMPTY" });
            }
            continue;
         }

         const minVal = getRangeMin(fieldDef);
         const maxVal = getRangeMax(fieldDef, row, fieldDefById, apiColumns);

         const numberValue = Number(
            typeof value === "object" ? (value.value ?? value.originalValue) : value,
         );

         if (Number.isFinite(numberValue)) {
            if (minVal != null && numberValue < minVal) {
               problems.push({ fieldId, reason: "MIN", min: minVal });
            }
            if (maxVal != null && numberValue > maxVal) {
               problems.push({ fieldId, reason: "MAX", max: maxVal });
            }
         }
      }

      return problems;
   };

   const isRowFilledByRequiredFields = (row: RowRecord, requiredFields: any[]) => {
      if (requiredFields.length === 0) return false;

      return requiredFields.every((fieldDef) => {
         const fieldId = String(fieldDef.id);
         const fieldLabel = getFieldLabel(fieldId);
         const value = getRowFieldValue(row, fieldId, fieldLabel);
         return hasValue(value);
      });
   };

   const validate = useCallback(() => {
      const dataRow = dataSource.length > 0 ? dataSource[0] : undefined;

      const rangedFields = Object.values(fieldDefById ?? {}).filter((fieldDef) => {
         const min = getRangeMin(fieldDef);
         const max = getRangeMax(fieldDef, dataRow, fieldDefById, apiColumns);
         return min != null || max != null;
      });

      if (!rangedFields.length) {
         return { ok: true };
      }

      const requiredFields = getRequiredFields(rangedFields);
      const hasAnyRequiredField = requiredFields.length > 0;

      const optionsRange = getOptionsRangeForListTable();
      const selectedRows = hasCheckboxColumn
         ? dataSource.filter((row) => selectedIds?.includes(String(row.id)))
         : [];

      // CASE 1: OptionsRangeDecorator + checkboxes
      if (optionsRange.has && hasCheckboxColumn) {
         const selectedCount = selectedRows.length;

         if (selectedCount === 0) {
            const minimumSelected = optionsRange.min == null ? 1 : optionsRange.min;
            return { ok: false, message: [`Zaznacz minimum ${minimumSelected} wierszy.`] };
         }

         if (hasAnyRequiredField) {
            if (optionsRange.min != null && selectedCount < optionsRange.min) {
               return { ok: false, message: [`Zaznacz minimum ${optionsRange.min} wierszy.`] };
            }
            if (optionsRange.max != null && selectedCount > optionsRange.max) {
               return { ok: false, message: [`Zaznacz maksymalnie ${optionsRange.max} wierszy.`] };
            }
         }

         const details: Record<string, RangeValidationProblem[]> = {};
         for (const row of selectedRows) {
            const rowProblems = validateRow(row, rangedFields);
            if (rowProblems.length > 0) {
               details[String(row.id)] = rowProblems;
            }
         }

         return Object.keys(details).length === 0
            ? { ok: true }
            : { ok: false, details, message: buildMessages(details) };
      }

      // CASE 2: OptionsRangeDecorator + without checkboxes
      if (optionsRange.has && !hasCheckboxColumn) {
         const touchedRows = dataSource.filter((row) => isRowTouched(row, rangedFields));

         const filledRows = hasAnyRequiredField
            ? touchedRows.filter((row) => isRowFilledByRequiredFields(row, requiredFields))
            : touchedRows;

         const filledCount = filledRows.length;

         const messages: string[] = [];

         if (hasAnyRequiredField) {
            if (optionsRange.min != null && filledCount < optionsRange.min) {
               messages.push(`Minimalna ilość wypełnionych wierszy: ${optionsRange.min} `);
            }
            if (optionsRange.max != null && filledCount > optionsRange.max) {
               messages.push(`Maksymalna ilość wypełnionych wierszy: ${optionsRange.max}`);
            }
         }

         const details: Record<string, RangeValidationProblem[]> = {};
         for (const row of filledRows) {
            const rowProblems = validateRow(row, rangedFields);
            if (rowProblems.length > 0) {
               details[String(row.id)] = rowProblems;
            }
         }

         const validRowIds = filledRows
            .filter((row) => !details[String(row.id)])
            .map((row) => String(row.id));

         if (messages.length === 0 && Object.keys(details).length === 0) {
            return { ok: true, validRowIds };
         }

         const allMessages =
            Object.keys(details).length > 0 ? [...messages, ...buildMessages(details)] : messages;

         return { ok: false, validRowIds, message: allMessages };
      }

      // CASE 3: without OptionsRangeDecorator + checkboxes
      if (!optionsRange.has && hasCheckboxColumn) {
         if (selectedRows.length === 0) {
            return { ok: true };
         }

         const details: Record<string, RangeValidationProblem[]> = {};
         for (const row of selectedRows) {
            const rowProblems = validateRow(row, rangedFields);
            if (rowProblems.length > 0) {
               details[String(row.id)] = rowProblems;
            }
         }

         return Object.keys(details).length === 0
            ? { ok: true }
            : { ok: false, details, message: buildMessages(details) };
      }

      // CASE 4: without OptionsRangeDecorator + without checkboxes
      const details: Record<string, RangeValidationProblem[]> = {};
      for (const row of dataSource) {
         const rowProblems = validateRow(row, rangedFields);
         if (rowProblems.length > 0) {
            details[String(row.id)] = rowProblems;
         }
      }

      return Object.keys(details).length === 0
         ? { ok: true }
         : { ok: false, details, message: buildMessages(details) };
   }, [fieldDefById, dataSource, hasCheckboxColumn, selectedIds, apiColumns]);

   const renderWithBoldField = (msg: string) => {
      const match = msg.match(/^(.*)\"(.+?)\"(.*)$/);
      if (!match) return msg;

      const [_, before, fieldName, after] = match;

      return (
         <>
            {before}
            <strong>{fieldName}</strong>
            {after}
         </>
      );
   };

   useEffect(() => {
      if (!registerListTableValidator) return;

      registerListTableValidator(field.id, validate);

      return () => {
         unregisterListTableValidator?.(field.id);
      };
   }, [registerListTableValidator, unregisterListTableValidator, field.id, validate]);

   const handleCellValueChange = useCallback(
      (record: RowRecord, col: Column, changedField: { id: string }, value: unknown) => {
         changeFieldsInfo?.(formGUID, { [field.id]: null });

         const displayValue = toDisplayValue(value);

         setEditedRow((prev) => ({
            ...prev,
            [col.label]: displayValue,
            __raw: {
               ...prev.__raw,
               [changedField.id]: value,
            },
         }));

         setDataSource((prev) =>
            prev.map((row) =>
               row.key === record.key
                  ? {
                       ...row,
                       __raw: {
                          ...(row.__raw ?? {}),
                          [changedField.id]: value,
                       },
                       [col.label]: displayValue,
                    }
                  : row,
            ),
         );
      },
      [changeFieldsInfo, formGUID, field.id],
   );

   const tableColumns: ColumnProps<RowRecord>[] = useMemo(() => {
      const cols: ColumnProps<RowRecord>[] = [];
      cols.push({
         title: "",
         key: "actions",
         width: 150,
         render: (_: unknown, record: RowRecord) => {
            const editable = record.key === editingRowKey;
            return editable ? (
               <Button buttonType="primary" onClick={() => handleSave(record.key)}>
                  {intl.formatMessage({ id: "global.save" })}
               </Button>
            ) : (
               <Button
                  buttonType="primary"
                  onClick={() => handleEdit(record)}
                  disabled={!!editingRowKey || isFieldDisabled(field)}
               >
                  {intl.formatMessage({ id: "global.edit" })}
               </Button>
            );
         },
      });

      if (hasCheckboxColumn) {
         cols.push({
            key: "checkbox",
            title: (
               <div className="list-widget-checkbox-main">
                  <Checkbox
                     checked={allChecked}
                     indeterminate={someChecked && !allChecked}
                     onChange={(e) => handleHeaderCheckboxChange(e.target.checked)}
                  />
               </div>
            ),
            render: (item: RowRecord) => {
               const isChecked = stringSelectedIds.includes(String(item.id));
               return (
                  <div className="list-widget-checkbox-main">
                     <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                           handleRowCheckboxChange(item, e.target.checked);
                        }}
                     />
                  </div>
               );
            },
         });
      }

      cols.push(
         ...visibleColumns.map((col) => ({
            title: col.label,
            dataIndex: col.label,
            key: col.techName,
            render: (_: unknown, record: RowRecord) => {
               const editable = record.key === editingRowKey;

               if (!editable) return record[col.label];

               const fieldDef = fieldDefById[String(col.techName)];
               if (!fieldDef) {
                  return record[col.label];
               }

               const rowFormGUID = getRowFormGUID(formGUID, record.id);
               const rawValue = record.__raw?.[col.techName] ?? record[col.label];
               const fieldForCell = buildFieldWithValue(fieldDef, rawValue);
               const key = `${record.id}:${fieldDef.id}`;

               return (
                  <span key={key} className="form-field">
                     {renderFormField(
                        fieldForCell,
                        rowFormGUID,
                        false,
                        ({ field: changedField, value }) =>
                           handleCellValueChange(record, col, changedField, value),
                        intl,
                     )}
                  </span>
               );
            },
         })),
      );

      return cols;
   }, [
      editingRowKey,
      intl,
      allChecked,
      someChecked,
      stringSelectedIds,
      hasCheckboxColumn,
      visibleColumns,
      fieldDefById,
      getRowFormGUID,
      buildFieldWithValue,
      formGUID,
      field,
      handleCellValueChange,
   ]);

   return (
      <div className="formV3-element-full" style={{ width: "100%" }}>
         <Table columns={tableColumns} dataSource={dataSource} scroll={{ x: "max-content" }} />
         {Array.isArray(fieldInfo) && fieldInfo.length > 0 ? (
            <ul className="validation-error">
               {fieldInfo.map((msg: string, idx: number) => (
                  <li key={idx}>{renderWithBoldField(msg)}</li>
               ))}
            </ul>
         ) : null}
      </div>
   );
};
